import { Link } from "react-router-dom";
import type { TreeMapPoint } from "@/types/cta-mapa-ui";
import { riscoConfig } from "@/config/cta-config";
import { reviewStatusLabel, tipoIndividuoLabel } from "@/config/cta-mapa-config";

interface MapDetailPanelProps {
  selectedTree: TreeMapPoint | null;
  /** Tecnicos que registraram vistoria neste exemplar (da listagem de vistorias). */
  tecnicos: string[];
  open: boolean;
  onClose: () => void;
}

function formatDate(value: string | null): string {
  if (!value) return "Sem vistoria";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

/**
 * Resumo do exemplar com o que o cadastro traz.  Condicao, medidas e recomendacao estao na FCA,
 * que as le da vistoria validada mais recente.
 */
export default function MapDetailPanel({ selectedTree, tecnicos, open, onClose }: MapDetailPanelProps) {
  if (!open || !selectedTree) return null;
  const t = selectedTree;
  const risco = t.classeRisco
    ? riscoConfig[t.classeRisco] || { badge: "bg-background-100 text-foreground-500", label: t.classeRisco }
    : { badge: "bg-background-100 text-foreground-400", label: "Sem avaliação de risco" };
  const endereco = [t.logradouro, t.bairro].filter(Boolean).join(" — ");

  return (
    <div className="w-[340px] bg-white border-l border-background-200/70 flex flex-col shrink-0 h-full shadow-lg z-30">
      <div className="flex items-center justify-between px-4 py-3 border-b border-background-200/70">
        <span className="text-sm font-semibold text-foreground-900 font-heading whitespace-nowrap">Exemplar arbóreo</span>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors" title="Fechar detalhes">
          <i className="ri-close-line text-foreground-500 text-base"></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <p className="text-xs text-foreground-500 font-mono mb-1">{t.codigoCta || "—"}</p>
          <h3 className="text-base font-bold text-foreground-900 font-heading">{t.nomePopular || "Espécie não identificada"}</h3>
          {t.nomeCientifico && <p className="text-xs text-foreground-500 italic">{t.nomeCientifico}</p>}
          <div className="mt-2 flex gap-1.5 flex-wrap">
            <span className={`badge text-[10px] ${risco.badge}`}>{t.classeRisco ? `Risco ${risco.label}` : risco.label}</span>
            {t.statusRevisao && (
              <span className="badge text-[10px] bg-background-100 text-foreground-600">Revisão: {reviewStatusLabel(t.statusRevisao)}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <InfoField label="Tipo" value={tipoIndividuoLabel(t.tipo)} />
          <InfoField label="Última vistoria" value={formatDate(t.ultimaVistoria)} />
        </div>

        <div className="space-y-1.5 pt-1 border-t border-background-200/70">
          <InfoRow icon="ri-map-pin-line" label="Localização" value={endereco || "Sem endereço informado"} />
          <InfoRow icon="ri-gps-line" label="Coordenadas" value={`${t.latitude.toFixed(6)}, ${t.longitude.toFixed(6)}`} />
          <InfoRow icon="ri-folder-chart-line" label="Projeto" value={t.projeto || "—"} />
          <InfoRow icon="ri-user-line" label="Técnicos com vistoria" value={tecnicos.length > 0 ? tecnicos.join(", ") : "—"} />
        </div>

        <div className="space-y-2 pt-2 border-t border-background-200/70">
          <Link to={`/cta/${t.id}`} className="w-full btn-primary text-xs py-2.5 whitespace-nowrap">
            <i className="ri-eye-line text-sm" aria-hidden="true"></i>
            Ver FCA completa
          </Link>
          <Link to={`/cta/${t.id}/inspecoes/nova`} className="w-full btn-outline text-xs py-2 whitespace-nowrap">
            <i className="ri-clipboard-line text-xs" aria-hidden="true"></i>
            Nova vistoria
          </Link>
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-background-50 rounded-md px-2.5 py-2">
      <p className="text-[10px] text-foreground-500">{label}</p>
      <p className="text-xs font-semibold text-foreground-900 whitespace-nowrap">{value}</p>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <i className={`${icon} text-foreground-400 text-xs mt-0.5 shrink-0`} aria-hidden="true"></i>
      <div className="min-w-0">
        <p className="text-[10px] text-foreground-500">{label}</p>
        <p className="text-xs text-foreground-800">{value}</p>
      </div>
    </div>
  );
}
